import axios from 'axios';

class DestinationsService {
  constructor() {
    this.googleApiKey = 'AIzaSyC1V_1rQ8CdCJyUDNr0tCF2Hg9TQb6cO0s';
    this.baseUrl = 'https://maps.googleapis.com/maps/api/place';
    this.cache = new Map();
    this.cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours
  }

  // Get popular destinations using Google Places API
  async getPopularDestinations() {
    try {
      console.log('DestinationsService: Starting to fetch destinations from Google Places API');
      
      // Check cache first
      const cacheKey = 'popular_destinations';
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        console.log('DestinationsService: Returning cached destinations', cached);
        return cached;
      }

      const destinations = new Set(); // Use Set to avoid duplicates
      
      // Multiple search strategies to get more destinations
      const searchStrategies = [
        // Strategy 1: Search for major cities worldwide
        {
          queries: [
            'major cities worldwide',
            'popular cities',
            'famous cities',
            'tourist cities',
            'capital cities'
          ],
          type: '(cities)'
        },
        // Strategy 2: Search for tourist attractions in different regions
        {
          queries: [
            'tourist attractions Europe',
            'tourist attractions Asia', 
            'tourist attractions America',
            'tourist attractions Africa',
            'tourist attractions Australia'
          ],
          type: 'tourist_attraction'
        },
        // Strategy 3: Search for specific popular destinations
        {
          queries: [
            'Dubai attractions',
            'Paris attractions',
            'London attractions',
            'Tokyo attractions',
            'New York attractions',
            'Barcelona attractions',
            'Rome attractions',
            'Amsterdam attractions',
            'Singapore attractions',
            'Sydney attractions',
            'Mumbai attractions',
            'Delhi attractions',
            'Bangalore attractions',
            'Chennai attractions',
            'Kolkata attractions',
            'Hyderabad attractions',
            'Pune attractions',
            'Ahmedabad attractions',
            'Jaipur attractions',
            'Goa attractions'
          ],
          type: 'tourist_attraction'
        }
      ];

      // Process each strategy
      for (const strategy of searchStrategies) {
        console.log(`DestinationsService: Processing strategy with ${strategy.queries.length} queries`);
        
        // Process queries in batches of 3 to avoid rate limiting
        for (let i = 0; i < strategy.queries.length; i += 3) {
          const batch = strategy.queries.slice(i, i + 3);
          
          const promises = batch.map(async (query) => {
            try {
              const url = `${this.baseUrl}/textsearch/json`;
              const params = {
                query: query,
                type: strategy.type,
                key: this.googleApiKey,
                language: 'en'
              };

              console.log(`DestinationsService: Fetching for query: ${query}`);
              
              const response = await axios.get(url, { 
                params,
                timeout: 15000
              });
              
              if (response.data.status === 'OK' && response.data.results.length > 0) {
                const places = response.data.results
                  .map(place => {
                    // Extract city name from place name or address
                    let name = place.name;
                    
                    // If it's a tourist attraction, try to extract city from address
                    if (strategy.type === 'tourist_attraction' && place.formatted_address) {
                      const addressParts = place.formatted_address.split(',');
                      if (addressParts.length > 1) {
                        // Take the second part (usually city) or first part if it looks like a city
                        const cityPart = addressParts[1]?.trim() || addressParts[0]?.trim();
                        if (cityPart && cityPart.length < 50) { // Reasonable city name length
                          name = cityPart;
                        }
                      }
                    }
                    
                    return name;
                  })
                  .filter(name => name && name.length > 0 && name.length < 100)
                  .slice(0, 5); // Get top 5 from each query
                
                console.log(`DestinationsService: Found ${places.length} places for query: ${query}`);
                return places;
              } else {
                console.warn(`DestinationsService: No results for query: ${query}, status: ${response.data.status}`);
                return [];
              }
            } catch (error) {
              console.error(`Error fetching for query ${query}:`, error.message);
              return [];
            }
          });

          const results = await Promise.all(promises);
          results.forEach(places => {
            places.forEach(place => destinations.add(place));
          });
          
          console.log(`DestinationsService: Total unique destinations so far: ${destinations.size}`);
          
          // Add delay between batches
          if (i + 3 < strategy.queries.length) {
            await new Promise(resolve => setTimeout(resolve, 2000)); // 2 second delay
          }
        }
      }

      // Convert Set to Array and clean up the data
      let finalDestinations = Array.from(destinations)
        .filter(dest => {
          // Filter out very long names and common unwanted terms
          return dest.length > 2 && 
                 dest.length < 50 && 
                 !dest.toLowerCase().includes('hotel') &&
                 !dest.toLowerCase().includes('restaurant') &&
                 !dest.toLowerCase().includes('shopping') &&
                 !dest.toLowerCase().includes('mall') &&
                 !dest.toLowerCase().includes('airport') &&
                 !dest.toLowerCase().includes('station');
        })
        .slice(0, 100); // Limit to 100 destinations

      if (finalDestinations.length === 0) {
        throw new Error('No destinations were fetched from API');
      }

      console.log('DestinationsService: Final destinations from API', finalDestinations);
      
      // Cache the result
      this.setCache(cacheKey, finalDestinations);
      
      const result = {
        success: true,
        data: finalDestinations,
        message: `Successfully fetched ${finalDestinations.length} destinations from Google Places API`
      };
      
      console.log('DestinationsService: Returning result', result);
      return result;
    } catch (error) {
      console.error('DestinationsService: Error fetching destinations:', error);
      throw error;
    }
  }

  // Get destinations based on search query (for autocomplete)
  async searchDestinations(query) {
    try {
      if (!query || query.length < 2) {
        return { success: true, data: [], message: 'Query too short' };
      }

      const cacheKey = `search_${query.toLowerCase()}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      console.log(`DestinationsService: Searching for destinations matching: ${query}`);

      // For now, use fallback destinations due to CORS restrictions
      // In production, you would need a backend proxy server
      const fallbackDestinations = this.getFallbackDestinations(query);
      
      const result = {
        success: true,
        data: fallbackDestinations,
        message: 'Destinations loaded from local database'
      };

      this.setCache(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Error searching destinations:', error);
      
      // Fallback to a simple list of popular destinations
      const fallbackDestinations = this.getFallbackDestinations(query);
      
      return {
        success: true,
        data: fallbackDestinations,
        message: 'Using fallback destinations due to API error'
      };
    }
  }

  // Fallback destinations when API fails
  getFallbackDestinations(query) {
    const allDestinations = [
      'Dubai', 'Paris', 'London', 'Tokyo', 'New York', 'Barcelona',
      'Rome', 'Amsterdam', 'Singapore', 'Sydney', 'Bangkok', 'Istanbul',
      'Mumbai', 'Delhi', 'Bangalore', 'Chennai', 'Kolkata', 'Hyderabad',
      'Pune', 'Ahmedabad', 'Jaipur', 'Goa', 'Kerala', 'Rajasthan',
      'Las Vegas', 'Los Angeles', 'San Francisco', 'Miami', 'Chicago',
      'Boston', 'Seattle', 'Denver', 'Atlanta', 'Dallas', 'Houston',
      'Phoenix', 'Philadelphia', 'Detroit', 'Minneapolis', 'Cleveland',
      'Madrid', 'Berlin', 'Vienna', 'Prague', 'Budapest', 'Warsaw',
      'Stockholm', 'Copenhagen', 'Oslo', 'Helsinki', 'Zurich', 'Geneva',
      'Brussels', 'Antwerp', 'Rotterdam', 'The Hague', 'Utrecht',
      'Melbourne', 'Perth', 'Adelaide', 'Brisbane', 'Gold Coast',
      'Hong Kong', 'Macau', 'Taipei', 'Seoul', 'Busan', 'Incheon',
      'Manila', 'Jakarta', 'Kuala Lumpur', 'Penang', 'Malacca',
      'Cairo', 'Alexandria', 'Casablanca', 'Marrakech', 'Tunis',
      'Cape Town', 'Johannesburg', 'Durban', 'Pretoria', 'Port Elizabeth',
      'Lagos', 'Abuja', 'Kano', 'Ibadan', 'Port Harcourt',
      'Nairobi', 'Mombasa', 'Kampala', 'Dar es Salaam', 'Addis Ababa',
      'São Paulo', 'Rio de Janeiro', 'Brasília', 'Salvador', 'Fortaleza',
      'Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata',
      'Santiago', 'Valparaíso', 'Concepción', 'Antofagasta', 'Temuco',
      'Lima', 'Arequipa', 'Trujillo', 'Chiclayo', 'Piura',
      'Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena',
      'Caracas', 'Maracaibo', 'Valencia', 'Barquisimeto', 'Maracay',
      'Mexico City', 'Guadalajara', 'Monterrey', 'Puebla', 'Tijuana',
      'Montreal', 'Toronto', 'Vancouver', 'Calgary', 'Edmonton',
      'Ottawa', 'Winnipeg', 'Quebec City', 'Hamilton', 'London Ontario'
    ];

    // Filter destinations that match the query
    const filtered = allDestinations.filter(dest => 
      dest.toLowerCase().includes(query.toLowerCase())
    );

    return filtered.slice(0, 10);
  }

  // Get nearby places
  async getNearbyDestinations(lat, lng, radius = 50000) {
    try {
      const cacheKey = `nearby_${lat}_${lng}_${radius}`;
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      const url = `${this.baseUrl}/nearbysearch/json`;
      const params = {
        location: `${lat},${lng}`,
        radius: radius,
        type: 'tourist_attraction',
        key: this.googleApiKey,
        language: 'en'
      };

      const response = await axios.get(url, { params });
      
      if (response.data.status === 'OK') {
        const destinations = response.data.results
          .map(place => place.name)
          .filter((name, index, self) => self.indexOf(name) === index)
          .slice(0, 20);

        const result = {
          success: true,
          data: destinations,
          message: 'Nearby destinations fetched successfully'
        };

        this.setCache(cacheKey, result);
        return result;
      } else {
        throw new Error(`Google Places API error: ${response.data.status}`);
      }
    } catch (error) {
      console.error('Error fetching nearby destinations:', error);
      throw error;
    }
  }

  // Cache management
  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }
    return null;
  }

  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clearCache() {
    this.cache.clear();
  }

  // Force refresh destinations (bypass cache)
  async refreshDestinations() {
    this.clearCache();
    return await this.getPopularDestinations();
  }
}

export const destinationsService = new DestinationsService();